import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, ErrorBanner, PageHeader } from '@zarya/ui';
import { isApiError } from '@app/api/ApiError';
import { errorView } from '@app/errors';
import { dataHour } from '@app/format';
import { t, tn } from '@app/i18n';
import { IdentityListPane } from '@app/shared/IdentityListPane';
import painel from '@app/shared/IdentityListPane/IdentityListPane.module.css';
import { useIdentities } from '../Contracts/Contracts.api';
import { AuthorizationCard } from './AuthorizationCard';
import { DeployCard } from './DeployCard';
import { RefreshButton } from './RefreshButton';
import { SecurityReadyCard } from './SecurityReadyCard';
import {
  useInvalidateReadiness,
  useRuntimeAggregate,
  useRuntimeAggregateCached,
  useRuntimeStatus,
  type RuntimeAggregate,
} from './Runtime.api';
import s from './Runtime.module.css';

/**
 * T15 — Runtime Status (FR-090 a FR-093).
 *
 * "Se o estado aprovado está de fato materializado no cluster." Uma identidade
 * por vez; o agregado do tenant fica na linha de metadados, e o "desejado ×
 * publicado" é do tenant inteiro — o PDP recebe um documento só.
 *
 * A identidade se escolhe na **lista à esquerda** desde 30/08 (pedido do
 * mrolim): o mesmo `IdentityListPane` de T13 e T14, no lugar do combobox que
 * ficava no cabeçalho. Esta era a única das três telas que ainda selecionava de
 * um jeito próprio.
 *
 * Sem **Simular deploy + atestação** e sem o check **Gateway (PEP)**: os dois
 * saíram do protótipo no §4 do PRD.
 *
 * ## Carrega rápido, busca em segundo plano (04/09, pedido do mrolim)
 *
 * A leitura viva do agregado leva ~11 s no laboratório, e a tela ficava em
 * branco esperando por ela. Agora são **duas consultas em paralelo**: a rápida
 * (`?source=cache`, o que o backend já conhecia) desenha a tela na hora com o
 * último estado conhecido e o aviso «buscando no cluster…»; a viva substitui
 * tudo quando chega — e, ao chegar, **invalida o que lê o mesmo cache** no resto
 * do console (`useInvalidateReadiness`): lista de identidades, Visão geral,
 * card Estado de T13/T14, selo da fila, chip da moldura. Era a ausência dessa
 * invalidação que fazia a lista «só atualizar com F5».
 *
 * Cache frio (`as_of: null`) **não** é dado: a tela não diz «nenhuma identidade»
 * sobre um cluster que ninguém leu — só a leitura viva pode dizer isso.
 */

/**
 * O que a linha diz quando o executor não conhece o workload.
 *
 * A lista da esquerda é a de identidades do tenant — a mesma de T13/T14 —, e o
 * agregado do runtime só traz as que têm workload no cluster. As que faltam
 * aparecem **desabilitadas com o motivo**, em vez de sumirem: sumir faria a
 * pessoa procurar por uma identidade que ela sabe que existe. É o mesmo
 * mecanismo que T14 usa para a FR-071, pela prop que o componente já oferece —
 * nada mudou no componente.
 */


/**
 * `{n} identidades · {p} prontas · {d} divergentes · lido {hora}` (FR-090).
 * Sobre o cache: `último estado conhecido · lido {hora}`; enquanto a viva não
 * chega, `· buscando no cluster…` no fim.
 */
function metadata(aggregate: RuntimeAggregate | undefined, searching: boolean): string {
  if (!aggregate) return t('runtimeStatus.page.meta.reading');
  const parts = [
    tn('runtimeStatus.page.meta.identities', aggregate.total),
    tn('runtimeStatus.page.meta.ready', aggregate.ready),
    tn('runtimeStatus.page.meta.divergent', aggregate.divergences),
    aggregate.stale
      ? t('runtimeStatus.page.meta.lastKnown', { when: dataHour(aggregate.as_of) ?? '—' })
      : t('runtimeStatus.page.meta.read', { when: dataHour(aggregate.read_at) ?? '—' }),
  ];
  if (searching) parts.push(t('runtimeStatus.page.meta.searching'));
  return parts.join(' · ');
}

/** O motivo que o botão Atualizar mostra no tooltip quando a leitura falha. */
function refreshReason(error: unknown): string {
  if (isApiError(error)) return errorView(error).mensagem;
  if (error instanceof Error && error.message) return error.message;
  return t('runtimeStatus.page.refreshFailed');
}

export function RuntimePage() {
  // As duas leituras do agregado, em paralelo: a viva (o que o cluster diz
  // agora) e a rápida (o que o backend já sabia). A mesma `queryKey` viva de
  // T9 — quem chega de lá já a tem em cache.
  const live = useRuntimeAggregate();
  const cached = useRuntimeAggregateCached();
  // A mesma leitura de T13/T14 — mesma `queryKey`, então quem chega de lá já a
  // tem em cache. Dispara em paralelo com o agregado (as duas no topo do
  // componente): não entra no caminho crítico dos ~11 s, que é a leitura do
  // executor.
  const identidades = useIdentities();
  const invalidateReadiness = useInvalidateReadiness();
  const [escolhida, setEscolhida] = useState<string | null>(null);

  // O que a tela desenha: a viva quando já chegou; senão o que o backend já
  // conhecia — e só se ele conhecia algo (`as_of`). Cache frio fica em «buscando».
  const known = cached.data?.as_of ? cached.data : undefined;
  const aggregate = live.data ?? known;
  const searching = live.isFetching;

  // Quando a leitura viva chega, tudo que lê o cache de prontidão está velho —
  // aqui (lista da esquerda) e nas outras telas. Uma vez por leitura, pelo
  // `dataUpdatedAt`, e não em `onSuccess`: o react-query v5 não o tem em
  // `useQuery`, e o efeito também cobre o `refetch` do botão.
  const lastLiveAt = useRef(0);
  useEffect(() => {
    if (!live.isSuccess || live.dataUpdatedAt === lastLiveAt.current) return;
    lastLiveAt.current = live.dataUpdatedAt;
    void invalidateReadiness();
  }, [live.isSuccess, live.dataUpdatedAt, invalidateReadiness]);

  const aplicacoes = useMemo(() => aggregate?.applications ?? [], [aggregate]);
  const lista = identidades.data ?? [];
  // Sem escolha explícita, abre na primeira identidade que o executor conhece —
  // o mesmo padrão de antes do combobox sair.
  const identityId = escolhida ?? aplicacoes[0]?.identity_id ?? null;
  const status = useRuntimeStatus(identityId);

  // Enquanto o status vivo da identidade não chega, o card mostra o que o
  // agregado (vivo ou em cache) já sabe dela — sem o certificado, que só o
  // status lê. É o «estado que já se conhece»; o card diz que está buscando.
  const knownStatus = useMemo(
    () => aplicacoes.find((item) => item.identity_id === identityId),
    [aplicacoes, identityId],
  );
  const statusShown = status.data ?? knownStatus;
  const statusStale = !status.data && Boolean(knownStatus);

  const withWorkload = new Set(aplicacoes.map((item) => item.identity_id));

  // O clique **força a leitura viva** do agregado e do status em foco — não há
  // janela no caminho `source=live`; a de 15 s é só do `EnsureRead` de T4/T5.
  // `refetch` resolve mesmo em erro; o erro vai para o botão pelo `throw`.
  const refresh = useCallback(async () => {
    const results = await Promise.all([live.refetch(), identityId ? status.refetch() : null]);
    for (const result of results) {
      if (result?.error) throw result.error;
    }
  }, [live, status, identityId]);

  return (
    <>
      <PageHeader
        title={t('runtimeStatus.page.title')}
        tip={t('runtimeStatus.page.purpose')}
        icon="gauge"
        updatedLabel={metadata(aggregate, searching)}
        // O combobox de identidade saiu daqui em 30/08 — quem escolhe agora é a
        // lista à esquerda. O Atualizar (PRD §4 T15) fica: ele relê o agregado e
        // o status da identidade em foco, e diz em que pé está (04/09).
        actions={<RefreshButton onRefresh={refresh} errorReason={refreshReason} />}
      />

      {live.isError ? (
        <ErrorBanner
          error={live.error}
          title={t('runtimeStatus.page.error.aggregate')}
          onRetry={() => void live.refetch()}
        />
      ) : null}

      {live.isSuccess && live.data.applications.length === 0 ? (
        <p className={s.vazioTexto}>{t('runtimeStatus.page.empty')}</p>
      ) : null}

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('runtimeStatus.page.error.identities')}
          onRetry={() => void identidades.refetch()}
          hideLink
        />
      ) : null}

      {identityId ? (
        <div className={painel.layout}>
          <IdentityListPane
            identidades={lista}
            selecionada={identityId}
            onEscolher={setEscolhida}
            carregando={identidades.isLoading}
            tip={t('runtimeStatus.page.list.tip')}
            motivoIndisponivel={(identidade) =>
              withWorkload.has(identidade.id) ? null : t('runtimeStatus.page.withoutWorkload')
            }
          />

          <div className={painel.detalhe}>
            <div className={s.colunas}>
              <SecurityReadyCard
                status={statusShown}
                stale={statusStale}
                carregando={status.isLoading || (statusStale && status.isFetching)}
                erro={status.isError ? status.error : null}
                onRetry={() => void status.refetch()}
              />
              <div className={s.coluna}>
                <AuthorizationCard />
                {/* Só o status vivo: uma ordem não nasce de dado que pode estar velho. */}
                <DeployCard status={status.data} />
                {/* PRD §4 T15 › card Ações: Ver conexão com o cluster (T20) · Abrir identidade (T5). */}
                <Card title={t('runtimeStatus.page.actions.title')}>
                  <div className={s.acoes}>
                    <Link className={s.acaoLink} to="/infra">
                      {t('runtimeStatus.page.actions.cluster')}
                    </Link>
                    <Link className={s.acaoLink} to={`/identidades?drawer=${identityId}`}>
                      {t('runtimeStatus.page.actions.identity')}
                    </Link>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
